import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CircularProgress from '@mui/material/CircularProgress'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import * as Sentry from '@sentry/react'
import classNames from 'classnames'
import _ from 'lodash'

import { RootState } from '../../../appReducer'
import { ChatStatus } from '../../../models/ChatStatus'
import PubNubService from '../../../services/PubNubService'
import { newObjectId } from '../../../util/objectId'
import { mainBlue } from '../../shared/styles/colors'
import { recordLiveWebChatSuggestionEditOutcome } from '../actions'
import useMessaging, { OnSendMessageParams, WHISPER_CHANNEL_PREFIX } from '../chat-tiles-container-hooks/useMessaging'
import ShortcutList from '../ShortcutList'

import AiSuggestionCard from './AiSuggestionCard'
import { normalizedEditDistance } from './editDistance'
import { classifySendFailure, SendResult } from './send-result'

import './ChatTextField.sass'

enum Mode {
    Chat = 'chat',
    Whisper = 'whisper',
}

type Props = {
    selectedChat: Models.ChatCenterSelectedChat
    isBanned: boolean | undefined
    shortcuts: Models.Shortcut[]
    refreshConversationInProgress: boolean
    focused?: boolean
    setPendingMessage: (message: Models.Message) => void
    markMessageFailed: (messageId: string) => void
    refreshConversation: () => void
}

type InitializeOnStartTyping = () => void

type AiEditProvenance = {
    channelName: string
    effectId: string
    suggestionText: string
    failedText?: string
}

const ChatTextFieldTheme = createTheme({
    components: {
        MuiInput: {
            styleOverrides: {
                underline: {
                    '&:after': {
                        display: 'none',
                    },
                    '&:before': {
                        display: 'none',
                    },
                },
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                root: { color: mainBlue },
            },
        },
    },
})

const ChatTextField = (props: Props) => {
    const { selectedChat, shortcuts, isBanned, focused, refreshConversationInProgress, refreshConversation } = props
    const dispatch = useDispatch()
    const account = useSelector((state: RootState) => state.app.self && state.app.self.account)
    const [text, setText] = useState<string>('')
    const [mode, setMode] = useState<Mode>(Mode.Chat)
    const [filteredShortcuts, setFilteredShortcuts] = useState<Models.Shortcut[]>([])
    const [selectedShortcut, setSelectedShortcut] = useState<number>(0)

    const selectedChatChannelName = selectedChat.channelName
    const initializeOnStartTypingRef = useRef<InitializeOnStartTyping | null>(null)
    const messageInputTextField = useRef<HTMLInputElement>()
    const aiEditProvenanceRef = useRef<AiEditProvenance | null>(null)

    const pubnubService = PubNubService.getInstance(account?.id || 'none')

    const { onSendMessage } = useMessaging()

    useEffect(() => {
        if (focused) {
            setTimeout(() => messageInputTextField.current?.focus(), 500)
        }
    }, [focused])

    const resizeTextArea = useCallback(() => {
        resetTextAreaHeight()
        const textarea: any = messageInputTextField?.current
        const scrollHeight = textarea.scrollHeight
        const minHeight = 61
        const maxHeight = 241

        let newHeight
        if (scrollHeight >= minHeight) {
            newHeight = scrollHeight <= maxHeight ? scrollHeight : maxHeight
        } else {
            newHeight = minHeight
        }

        textarea.style.height = `${newHeight}px`
    }, [])

    useEffect(() => {
        resizeTextArea()
    }, [text, resizeTextArea])

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const key = event.key

        if (filteredShortcuts.length > 0) {
            if (key === 'ArrowUp') {
                event.preventDefault()
                const shortcutIndex = selectedShortcut <= 0 ? filteredShortcuts.length - 1 : selectedShortcut - 1

                setText(`/${filteredShortcuts[shortcutIndex].name}`)
                setSelectedShortcut(shortcutIndex)
            }

            if (key === 'ArrowDown') {
                event.preventDefault()
                const shortcutIndex = selectedShortcut >= filteredShortcuts.length - 1 ? 0 : selectedShortcut + 1

                setText(`/${filteredShortcuts[shortcutIndex].name}`)
                setSelectedShortcut(shortcutIndex)
            }
        }

        if (key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
        }
    }

    const onKeyUp = async (event: React.KeyboardEvent<HTMLDivElement>) => {
        const { selectedChat } = props
        const key = event.key
        const textTrimmed = text.trim()

        if (!textTrimmed) {
            closeShortcuts()
            onStopTyping()
            return
        }

        if (mode !== Mode.Whisper && key !== 'Backspace' && key !== 'Delete' && initializeOnStartTypingRef.current) {
            initializeOnStartTypingRef.current()
        }

        if (key === 'Enter' && !event.shiftKey) {
            const channel = mode === Mode.Whisper ? `whisper_${selectedChat.channelName}` : selectedChat.channelName

            // If the shortcut list is still open don't send the text on 'Enter', but select the correct shortcut
            if (filteredShortcuts.length > 0) {
                insertShortcut(text)
                return
            }

            const sentText = text
            const messageId = newObjectId(32)
            // Take the provenance BEFORE the await: a fresh Edit-click during the round trip must
            // not be stamped with this message's distance, and this attempt must not read that
            // newer Edit's provenance when it resolves.
            const attempt = takeProvenanceForAttempt(sentText)

            if (mode !== Mode.Whisper) {
                setPendingMessage(messageId, sentText)
            }

            setText('')
            resetTextAreaHeight()
            const result = await sendMessage({ channel, message: sentText, chatMetadata: selectedChat, messageId })
            postEditOutcomeIfPresent(sentText, result, attempt)
        }
    }

    const setPendingMessage = (messageId: string, text: string) => {
        props.setPendingMessage({
            id: messageId,
            text: text,
            is_patient: false,
            sender_id: pubnubService.getChatterId(),
            type: 'text',
            status: 'pending',
            timetoken: (Date.now() * 10000).toString(),
        })
    }

    const onStartTyping = useCallback(() => {
        pubnubService.setState(selectedChatChannelName, { is_typing: true })
    }, [selectedChatChannelName])

    const onStopTyping = async () => {
        initializeOnStartTypingRef.current = _.once(onStartTyping)
        return pubnubService.setState(selectedChat.channelName, { is_typing: false })
    }

    useEffect(() => {
        initializeOnStartTypingRef.current = _.once(onStartTyping)
    }, [onStartTyping])

    const sendMessage = async (sendMessageParams: OnSendMessageParams): Promise<SendResult> => {
        const { channel, message, chatMetadata, messageId } = sendMessageParams
        try {
            await onStopTyping()
            const value = await onSendMessage({ channel, message, chatMetadata, messageId })
            if (channel.startsWith(WHISPER_CHANNEL_PREFIX)) {
                return { kind: 'published', record: null }
            }
            if (
                value &&
                typeof value === 'object' &&
                typeof (value as ApiV2.Amplify.PostLiveWebChatConversationEventRecord).productConversationSequence ===
                    'number'
            ) {
                return {
                    kind: 'admitted',
                    record: value as ApiV2.Amplify.PostLiveWebChatConversationEventRecord,
                }
            }
            return { kind: 'unknown' }
        } catch (err) {
            // A refused/failed admission call leaves the optimistic bubble pending forever
            // otherwise. markMessageFailed stays in this catch, before classification.
            if (messageId) {
                props.markMessageFailed(messageId)
            }
            return { kind: classifySendFailure(err) }
        }
    }

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newText = event.target.value

        if (newText.charAt(0) === '/') {
            filterShortcuts(newText.substr(1)) // removes the '/' character
        }

        setText(newText)
    }
    const resetTextAreaHeight = () => {
        const textarea: any = messageInputTextField?.current
        textarea.style.height = '61px'
    }

    const filterShortcuts = (key: string) => {
        let newList: Models.Shortcut[] = []

        newList = shortcuts.filter((shortcut: Models.Shortcut) => {
            const lowercaseShortcut = shortcut.name.toLowerCase()
            const lowercaseKey = key.toLowerCase()

            return lowercaseShortcut.indexOf(lowercaseKey) === 0
        })

        setFilteredShortcuts(newList)
    }

    const insertShortcut = (text: string) => {
        const shortcutIndex =
            filteredShortcuts.length > selectedShortcut ? selectedShortcut : filteredShortcuts.length - 1
        const shortcut = filteredShortcuts[shortcutIndex]
        text = (shortcut && shortcut.message) || text

        selectShortcut(text)
    }

    const selectShortcut = (text: string) => {
        setText(text)
        closeShortcuts()
        messageInputTextField?.current?.focus()
    }

    const closeShortcuts = () => {
        setSelectedShortcut(0)
        setFilteredShortcuts([])
    }

    // Same mechanism as selectShortcut - insert and focus, no send. Deliberately does not touch
    // `mode`: an AI suggestion is inserted where the specialist can see and edit it before
    // sending, without silently changing which channel it would go to (OQ-I).
    const onEditAiSuggestion = (text: string, effectId: string) => {
        aiEditProvenanceRef.current = { channelName: selectedChat.channelName, effectId, suggestionText: text }
        setText(text)
        messageInputTextField?.current?.focus()
    }

    type ProvenanceAttempt = { provenance: AiEditProvenance | null; isRetry: boolean }

    // Every send attempt takes ownership of whatever provenance is pending, synchronously, before
    // its await. Rule (one carry-over only): a fresh Edit's provenance rides the attempt; if that
    // attempt fails, it is put back once, tagged with the failed text; the NEXT attempt consumes it
    // regardless of outcome — as the retry if the text is byte-identical (it posts on success),
    // otherwise as an unrelated send (dropped, never posted). A second failure of the retry does
    // not put it back again, so the association can survive exactly one failed attempt.
    const takeProvenanceForAttempt = (attemptText: string): ProvenanceAttempt => {
        const provenance = aiEditProvenanceRef.current
        aiEditProvenanceRef.current = null
        if (!provenance) {
            return { provenance: null, isRetry: false }
        }
        if (provenance.failedText === undefined) {
            return { provenance, isRetry: false }
        }
        if (provenance.failedText === attemptText) {
            return { provenance, isRetry: true }
        }
        return { provenance: null, isRetry: false }
    }

    const postEditOutcomeIfPresent = (sentText: string, result: SendResult, attempt: ProvenanceAttempt) => {
        const { provenance, isRetry } = attempt
        if (!provenance) {
            return
        }
        if (result.kind !== 'admitted' && result.kind !== 'published') {
            // First failure: put it back once for a same-text retry — but only into an empty slot.
            // If the specialist clicked Edit on a newer suggestion during this send, that provenance
            // owns the ref now and must not be overwritten (its outcome is the one to post next).
            if (!isRetry && aiEditProvenanceRef.current === null) {
                aiEditProvenanceRef.current = { ...provenance, failedText: sentText }
            }
            return
        }
        const editDistance = normalizedEditDistance(provenance.suggestionText, sentText)
        if (editDistance == null) {
            return
        }
        dispatch(recordLiveWebChatSuggestionEditOutcome(provenance.effectId, editDistance)).catch((err: unknown) => {
            Sentry.captureException(err, {
                tags: { feature: 'ai-suggestion-edit-outcome' },
                extra: { effectId: provenance.effectId, editDistance },
            })
        })
    }

    // One-click accept: sends the suggestion text as-is, via the same path the Enter-key send
    // uses (`handleKeyDown`), rather than routing through the compose box at all. Also respects
    // the current `mode` rather than forcing the patient channel - same OQ-I reasoning as edit
    // above, so the two actions stay consistent with each other. A card Send never posts an edit
    // outcome (`accepted` is the card's own record), so it consumes and drops any pending
    // provenance up front — including a pending same-text retry — and never puts one back.
    const onSendAiSuggestion = async (text: string): Promise<SendResult> => {
        aiEditProvenanceRef.current = null
        const channel = mode === Mode.Whisper ? `whisper_${selectedChat.channelName}` : selectedChat.channelName
        const messageId = newObjectId(32)

        if (mode !== Mode.Whisper) {
            setPendingMessage(messageId, text)
        }

        return sendMessage({ channel, message: text, chatMetadata: selectedChat, messageId })
    }

    const isChatEnded = () => {
        const { value } = selectedChat.status
        return value === ChatStatus.PatientClosed.value
    }

    const opacityStyle = isBanned ? { opacity: 0.5 } : { opacity: 1 }

    return (
        <ThemeProvider theme={ChatTextFieldTheme}>
            <div className="chat-text-field">
                {selectedChat.copilotCardEnabled === true && (
                    <AiSuggestionCard
                        channelName={selectedChat.channelName}
                        onEdit={onEditAiSuggestion}
                        onSend={onSendAiSuggestion}
                        disabled={isBanned || isChatEnded()}
                    />
                )}
                <div className="chat-text-field__message-input" style={opacityStyle}>
                    {filteredShortcuts.length > 0 && (
                        <ShortcutList
                            filteredShortcuts={filteredShortcuts}
                            selectedShortcut={selectedShortcut}
                            selectShortcut={selectShortcut}
                            closeShortcuts={closeShortcuts}
                        />
                    )}

                    <div className="chat-buttons">
                        <button
                            className={classNames('button', { selected: mode === Mode.Chat })}
                            data-testid="chat-mode-chat"
                            onClick={() => setMode(Mode.Chat)}
                        >
                            Chat
                        </button>
                        <button
                            className={classNames('button', { selected: mode === Mode.Whisper })}
                            data-testid="chat-mode-whisper"
                            onClick={() => setMode(Mode.Whisper)}
                        >
                            Whisper
                        </button>
                        <span onClick={refreshConversation} className="refresh-button">
                            {!refreshConversationInProgress ? (
                                <i className="material-icons refresh-icon">refresh</i>
                            ) : (
                                <span className={`refresh-button-loader`}>
                                    <CircularProgress thickness={5} size={17} color="primary" variant="indeterminate" />
                                </span>
                            )}
                        </span>
                    </div>
                    <TextField
                        inputRef={messageInputTextField}
                        className="input-field"
                        inputProps={{ 'data-testid': 'chat-composer' }}
                        placeholder="Type your message here"
                        fullWidth={true}
                        multiline={true}
                        disabled={isBanned || isChatEnded()}
                        rows={5}
                        value={text}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        onKeyUp={onKeyUp}
                    />
                </div>

                {isBanned && (
                    <div className="centered-ban-message-red message-red">
                        {selectedChat.ip}
                        <br />
                        Banned
                    </div>
                )}
            </div>
        </ThemeProvider>
    )
}

export default ChatTextField
